import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'react-hot-toast';
import { AuthContext } from '../App';
import { Save, Mail, DollarSign, Calendar, Info } from 'lucide-react';

const ConfigEmailsRespuesta = () => {
  const { API } = useContext(AuthContext);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  
  const [config, setConfig] = useState({
    // Fechas
    fecha_inicio_clases: '',
    fecha_cierre_inscripciones: '',
    periodo_academico: '',
    
    // Costos
    costo_matricula: '',
    costo_semestral: '',
    info_becas: '',
    formas_pago: '',
    
    // Información general
    requisitos_ingreso: '',
    modalidad: '',
    horarios: '',
    contacto_especifico: '',
    
    // Enlaces
    link_registro: '',
    link_material_informativo: ''
  });

  useEffect(() => {
    cargarConfiguracion();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      const response = await axios.get(`${API}/admin/config-emails-respuesta`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setCargando(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const guardarConfiguracion = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/admin/config-emails-respuesta`, config);
      toast.success('✅ Configuración guardada correctamente');
    } catch (error) {
      console.error('Error guardando configuración:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return (
      <div className="flex justify-center items-center h-64">
        <i className="fas fa-spinner fa-spin text-4xl text-blue-500"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            <Mail className="inline mr-2 text-blue-600" size={32} />
            Configuración de Emails de Respuesta
          </h1>
          <p className="text-gray-600 mt-2">
            Configura la información que se incluirá en las 3 plantillas de emails automáticos
          </p>
        </div>
        
        <Button
          onClick={guardarConfiguracion}
          disabled={guardando}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          {guardando ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2" size={18} />
              Guardar Configuración
            </>
          )}
        </Button>
      </div>

      {/* SECCIÓN: FECHAS */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b-4 border-orange-400">
          <div className="flex items-center">
            <Calendar className="mr-2 text-orange-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Fechas Importantes</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Usado en Plantilla 2: "Próximo Inicio de Clases"
          </p>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              📅 Fecha de Inicio de Clases
            </label>
            <Input
              type="text"
              value={config.fecha_inicio_clases}
              onChange={(e) => handleChange('fecha_inicio_clases', e.target.value)}
              placeholder="Ej: 15 de Marzo de 2025"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ⏰ Fecha de Cierre de Inscripciones
            </label>
            <Input
              type="text"
              value={config.fecha_cierre_inscripciones}
              onChange={(e) => handleChange('fecha_cierre_inscripciones', e.target.value)}
              placeholder="Ej: 10 de Marzo de 2025"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              📚 Periodo Académico
            </label>
            <Input
              type="text"
              value={config.periodo_academico}
              onChange={(e) => handleChange('periodo_academico', e.target.value)}
              placeholder="Ej: Primer Semestre 2025"
            />
          </div>
        </CardContent>
      </Card>

      {/* SECCIÓN: COSTOS */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b-4 border-green-400">
          <div className="flex items-center">
            <DollarSign className="mr-2 text-green-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Información de Costos</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Usado en Plantilla 3: "Información Completa con Costos"
          </p>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              💵 Costo de Matrícula
            </label>
            <Input
              type="text"
              value={config.costo_matricula}
              onChange={(e) => handleChange('costo_matricula', e.target.value)}
              placeholder="Ej: $50 USD o Gratis"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              💰 Costo Semestral
            </label>
            <Input
              type="text"
              value={config.costo_semestral}
              onChange={(e) => handleChange('costo_semestral', e.target.value)}
              placeholder="Ej: $300 USD/semestre"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🎓 Información de Becas
            </label>
            <Textarea
              value={config.info_becas}
              onChange={(e) => handleChange('info_becas', e.target.value)}
              placeholder="Ej: Ofrecemos becas del 50% para estudiantes con dedicación completa..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              💳 Formas de Pago
            </label>
            <Textarea
              value={config.formas_pago}
              onChange={(e) => handleChange('formas_pago', e.target.value)}
              placeholder="Ej: Transferencia bancaria, PayPal, tarjeta de crédito/débito..."
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* SECCIÓN: INFORMACIÓN GENERAL */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b-4 border-blue-400">
          <div className="flex items-center">
            <Info className="mr-2 text-blue-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Información General</h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Usado en Plantilla 1: "Información General" y otras plantillas
          </p>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ✅ Requisitos de Ingreso
            </label>
            <Textarea
              value={config.requisitos_ingreso}
              onChange={(e) => handleChange('requisitos_ingreso', e.target.value)}
              placeholder="Ej: Ser mayor de 18 años, tener certificado de estudios secundarios..."
              rows={4}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🏫 Modalidad
            </label>
            <Input
              type="text"
              value={config.modalidad}
              onChange={(e) => handleChange('modalidad', e.target.value)}
              placeholder="Ej: Presencial, Online, Híbrido"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              🕐 Horarios
            </label>
            <Textarea
              value={config.horarios}
              onChange={(e) => handleChange('horarios', e.target.value)}
              placeholder="Ej: Lunes a Viernes 18:00 - 21:00 hrs"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              📞 Contacto Específico
            </label>
            <Input
              type="text"
              value={config.contacto_especifico}
              onChange={(e) => handleChange('contacto_especifico', e.target.value)}
              placeholder="Ej: WhatsApp: +598 99 123 456"
            />
          </div>
        </CardContent>
      </Card>

      {/* SECCIÓN: ENLACES */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-4 border-purple-400">
          <h2 className="text-xl font-bold text-gray-900">🔗 Enlaces</h2>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              📝 Link de Registro
            </label>
            <Input
              type="url"
              value={config.link_registro}
              onChange={(e) => handleChange('link_registro', e.target.value)}
              placeholder="https://ibaconnect.info (opcional, usa URL principal por defecto)"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              📄 Link de Material Informativo
            </label>
            <Input
              type="url"
              value={config.link_material_informativo}
              onChange={(e) => handleChange('link_material_informativo', e.target.value)}
              placeholder="https://... (opcional)"
            />
          </div>
        </CardContent>
      </Card>

      {/* Botón Guardar (duplicado al final) */}
      <div className="flex justify-end">
        <Button
          onClick={guardarConfiguracion}
          disabled={guardando}
          size="lg"
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          {guardando ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2" size={18} />
              Guardar Configuración
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ConfigEmailsRespuesta;
